/**
 * Axios API client with auth, tenant, and correlation-id headers.
 * Auto-refreshes JWT on 401 once before failing.
 * Phase 2B — humanizes 403 responses with a friendly toast.
 */
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api/v1`;

const TOKEN_KEY = "viona.access_token";
const REFRESH_KEY = "viona.refresh_token";
const TENANT_KEY = "viona.tenant_slug";
const USER_KEY = "viona.user";

export const auth = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  getTenant: () => localStorage.getItem(TENANT_KEY) || "brimo",
  setTenant: (slug) => localStorage.setItem(TENANT_KEY, slug),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch (e) {
      return null;
    }
  },
  setSession: ({ access_token, refresh_token, user, tenant_slug }) => {
    if (access_token) localStorage.setItem(TOKEN_KEY, access_token);
    if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (tenant_slug) localStorage.setItem(TENANT_KEY, tenant_slug);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
  isAuthed: () => !!localStorage.getItem(TOKEN_KEY),
  // 10.3b: RBAC introspection helper. Owners see `*` — return true
  // for any specific-permission check. Otherwise scan the flat
  // permissions list attached to the cached /users/me payload.
  hasPermission: (perm) => {
    try {
      const u = JSON.parse(localStorage.getItem(USER_KEY) || "null");
      const perms = (u && u.permissions) || [];
      if (perms.includes("*")) return true;
      return perms.includes(perm);
    } catch (e) { return false; }
  },
};

function newCorrelationId() {
  return "c-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((cfg) => {
  cfg.headers = cfg.headers || {};
  const t = auth.getAccess();
  if (t) cfg.headers["Authorization"] = `Bearer ${t}`;
  const slug = auth.getTenant();
  if (slug) cfg.headers["X-Tenant-Id"] = slug;
  cfg.headers["X-Correlation-Id"] = newCorrelationId();
  return cfg;
});

let _refreshing = null;

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const orig = err.config;
    if (err?.response?.status === 401 && !orig._retried) {
      orig._retried = true;
      const rt = auth.getRefresh();
      if (!rt) {
        auth.clear();
        return Promise.reject(err);
      }
      _refreshing = _refreshing || axios.post(`${API_BASE}/auth/refresh`, { refresh_token: rt });
      try {
        const resp = await _refreshing;
        _refreshing = null;
        auth.setSession({ access_token: resp.data.access_token, refresh_token: resp.data.refresh_token });
        orig.headers["Authorization"] = `Bearer ${resp.data.access_token}`;
        return api(orig);
      } catch (e) {
        _refreshing = null;
        auth.clear();
        window.location.href = "/login";
        return Promise.reject(e);
      }
    }
    // Phase 2B — humanize permission denied; opt out with config.skipPermissionToast.
    if (err?.response?.status === 403 && !orig?.skipPermissionToast) {
      const d = err.response.data?.detail;
      if (d && typeof d === "object" && d.error === "permission_denied") {
        toast.error("You don't have permission to perform this action.", {
          description: d.required ? `Required permission: ${d.required}` : undefined,
        });
      }
    }
    return Promise.reject(err);
  }
);
